// Copyright (c) 2022 by Move Closer

import { Ref } from '@vue/composition-api'
import { Vue } from 'vue-property-decorator'

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export interface CarouselConfig {
  autoplay?: boolean

  /**
   * Determines whether side buttons of carousel should be visible.
   */
  hasButtons?: boolean

  /**
   * Determines whether bottom buttons (pagination) of carousel should be visible.
   */
  hasPagination?: boolean

  /**
   * Determines the amount of slides to be displayed per view / group
   */
  perPage: number
  perScroll?: number

  /**
   * If true, don't round down the perPage value to the nearest integer
   */
  disableFlooredPerPage?: boolean

  variableWidth?: boolean

  useTransform?: boolean

  inLoop?: boolean

  swipeToSlide?: boolean
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CarouselProps extends CarouselConfig {
  /**
   * Determines the autoplay behaviour for carousel.
   */
  autoplay?: boolean

  /**
   * Determines whether height should be auto adjusted.
   */
  autoHeight?: boolean

  /**
   * Detetrmines whether slides should be centered.
   */
  centralize?: boolean

  /**
   * Determines the delay behaviour for carousel.
   */
  delay?: number

  /**
   * If slides can be changed by swiping.
   */
  draggable?: boolean

  /**
   * Determines on which slide carousel starts.
   */
  initialSlide?: number

  /**
   * Determines whether carousel should slide in loop.
   */
  inLoop?: boolean

  /**
   * Determines whether swiper should be paused on hover.
   */
  pauseOnHover?: boolean

  responsive?: CarouselResponsiveConfig

  /**
   * Determines the direction of the carousel
   */
  vertical?: boolean

  variableWidth?: boolean
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface UseCarouselProvides {
  /**
   * Determines the responsiveness of the carousel based on breakpoints
   */
  carouselProps: Ref<CarouselProperties>

  /**
   * Reference for carousels HTMLElement.
   */
  carouselRef: Ref<Vue | null>

  /**
   * Determines the callback for the @init event in carousel.
   */
  onInit: () => void

  /**
   * Determines callback fot the carousels item click.
   */
  onClick: () => void

  /**
   * Determines the callback for the @swipe event in carousel.
   */
  onSwipe: () => void

  /**
   * Determines initial slides per page count in carousel.
   */
  slidesPerPage: Ref<number | null>

  /**
   * Determine the fn() to be fired in each child of the carousel. (To fix drag issue on element only)
   */
  handleOnItemClick: (e: Event) => void
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export enum CarouselBreakpoint {
  XLargeDesktop = 'xl-desktop',
  LargeDesktop = 'l-desktop',
  Desktop = 'desktop',
  Mobile = 'mobile'
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (original)
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl> (edited)
 */
export const carouselBreakpointOptions: Record<string, number> = {
  // As of VueSlickCarousel, this breakpoint means all devices below 4000
  // If not passed some big value, the defaults props (slidesToShow, slidesToScroll) will be applied
  // Passed to prevent default values to be applied.
  [CarouselBreakpoint.XLargeDesktop]: 4000,
  [CarouselBreakpoint.LargeDesktop]: 1800,
  [CarouselBreakpoint.Desktop]: 1384,
  [CarouselBreakpoint.Mobile]: 768
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface BreakpointDescription {
  /**
   * Breakpoint from which settings will be applied.
   */
  breakpoint: number
  /**
   * Set of carousel props for breakpoint above.
   */
  settings: BreakpointSettings
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface BreakpointSettings {
  autoplay?: boolean
  arrows?: boolean
  dots?: boolean
  slidesToScroll: number
  slidesToShow: number
  variableWidth?: boolean
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export type BreakpointsDescriptionSet = BreakpointDescription[]

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export type CarouselResponsiveConfig = {
  mobile: CarouselConfig
  desktop: CarouselConfig
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CarouselProperties {
  autoplay?: boolean
  autoHeight?: boolean
  arrows?: boolean
  dots?: boolean
  draggable?: boolean
  slidesToScroll?: number
  slidesToShow?: number
  swipeToSlide?: boolean
  variableWidth?: boolean
  responsive?: BreakpointsDescriptionSet
  useTransform?: boolean
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export interface CarouselMethods {
  /**
   * Slides to the next slide.
   */
  next: () => void

  /**
   * Pauses the slider.
   */
  pause: () => void

  /**
   * Plays the slider if paused.
   */
  play: () => void

  /**
   * Slides to the previous slide.
   */
  prev: () => void

  /**
   * Builds the breakpoints.
   */
  makeBreakpoints: () => void

  /**
   * Jumps to the passed in slide.
   */
  goTo: (slideIndex: number) => void

  /**
   * Clears the breakpoints.
   */
  clearBreakpoints: () => void
}

/**
 *
 * Basic Definition for the `VueSlickCarousel` component's properties.
 *
 * IMPORTANT!: This type should be used just to describe carousel's instance that generated via ref.
 *
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export type Carousel = CarouselMethods & CarouselProperties & Vue
